import { Component, Input, ChangeDetectorRef, inject } from '@angular/core';
import { LogError, Metadata } from '@memberjunction/core';
import { marketingMarketingRecommendationEntity } from 'mj_generatedentities';
import { ROLE_RANK, RoleTier } from '../../marketing-workspace.routes';
import { PillVariant } from '../../shared/pill.component';

type RecommendationsFilter = 'open' | 'applied' | 'dismissed' | 'all';

/**
 * A recommendation Detail can be free-form, but the marketing agents that
 * write adaptation proposals (ProposeSectionAdaptation) embed a
 * `--- Current Body ---` / `--- Proposed Body ---` diff inside it. When those
 * markers are present we split them out so the section can render the
 * current→proposed change readably instead of as one opaque blob.
 */
interface ParsedDetail {
    /** Everything before the diff markers (trigger, rationale, confidence). */
    preamble: string;
    /** The current section body, when the proposal carries a diff. */
    current: string | null;
    /** The proposed section body, when the proposal carries a diff. */
    proposed: string | null;
    /** Trailing "To apply: …" guidance, when present. */
    footer: string | null;
}

/**
 * Strategy / Recommendations — the human inbox for `Marketing Recommendations`
 * rows the marketing agents write: sequence kill/iterate learnings (Channel),
 * strategy section-adaptation proposals (Experiment), and other Plan/Content/
 * Spend nudges. Mirrors FoundationApprovalsSection's shape (KPI strip + filter
 * pills + per-row actions), reading the `recommendations` slice that flows down
 * from the workspace shell (already loaded as entity_object, so .Save() works).
 *
 * Apply / Dismiss here are STATUS FLIPS ONLY. A section-adaptation proposal
 * carries a current→proposed diff in its Detail; "Apply" marks the row Applied
 * and stamps the actor, but does NOT mutate the live Strategy Section — applying
 * the diff is a separate, deliberate edit in the strategy doc. The UI says so.
 */
@Component({
    standalone: false,
    selector: 'mkt-ws-strategy-recommendations',
    template: `
<mkt-ws-page-head
    [eyebrow]="'Strategy · Recommendations' + (companyName ? ' · ' + companyName : '')"
    title="Recommendations inbox."
    sub="Proposals the marketing agents surfaced — sequence learnings, strategy adaptations, channel nudges. Review, then Apply (adopt) or Dismiss.">
</mkt-ws-page-head>

<div *ngIf="isLoadingContent" class="rc__loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading…</div>

<ng-container *ngIf="!isLoadingContent">
    <!-- KPI bar -->
    <div class="rc__kpis">
        <div class="rc__kpi" [class.rc__kpi--alert]="openCount > 0" (click)="setFilter('open')">
            <div class="rc__kpi-value">{{ openCount }}</div>
            <div class="rc__kpi-label">Open</div>
        </div>
        <div class="rc__kpi" (click)="setFilter('applied')">
            <div class="rc__kpi-value">{{ appliedCount }}</div>
            <div class="rc__kpi-label">Applied</div>
        </div>
        <div class="rc__kpi" (click)="setFilter('dismissed')">
            <div class="rc__kpi-value">{{ dismissedCount }}</div>
            <div class="rc__kpi-label">Dismissed</div>
        </div>
        <div class="rc__kpi" (click)="setFilter('all')">
            <div class="rc__kpi-value">{{ recommendations.length }}</div>
            <div class="rc__kpi-label">Total</div>
        </div>
    </div>

    <!-- Filter pills -->
    <div class="rc__pills">
        <button class="rc__pill" [class.rc__pill--active]="filter === 'open'" (click)="setFilter('open')">
            Open · {{ openCount }}
        </button>
        <button class="rc__pill" [class.rc__pill--active]="filter === 'applied'" (click)="setFilter('applied')">
            Applied · {{ appliedCount }}
        </button>
        <button class="rc__pill" [class.rc__pill--active]="filter === 'dismissed'" (click)="setFilter('dismissed')">
            Dismissed · {{ dismissedCount }}
        </button>
        <button class="rc__pill" [class.rc__pill--active]="filter === 'all'" (click)="setFilter('all')">
            All · {{ recommendations.length }}
        </button>
    </div>

    <mkt-ws-coming-soon
        *ngIf="filteredRecommendations.length === 0"
        icon="fa-circle-check"
        title="All caught up"
        [body]="filter === 'open' ? 'No open recommendations. The agents will surface new ones as results come in.' : 'Nothing in the ' + filter + ' bucket.'">
    </mkt-ws-coming-soon>

    <div class="rc__list">
        <div *ngFor="let r of filteredRecommendations" class="rc__row" [class.rc__row--open]="expandedId === r.ID">
            <button type="button" class="rc__row-head" (click)="toggle(r.ID)">
                <mkt-ws-pill [variant]="categoryVariant(r.Category)">{{ r.Category }}</mkt-ws-pill>
                <mkt-ws-pill [variant]="statusVariant(r.Status)">{{ r.Status }}</mkt-ws-pill>
                <span class="rc__title">{{ r.Title }}</span>
                <i class="fa-solid" [class.fa-chevron-down]="expandedId !== r.ID" [class.fa-chevron-up]="expandedId === r.ID"></i>
            </button>

            <div *ngIf="expandedId === r.ID" class="rc__detail">
                <ng-container *ngIf="parsed(r) as p">
                    <div *ngIf="p.preamble" class="rc__pre">{{ p.preamble }}</div>

                    <!-- current → proposed diff, when the proposal carries one -->
                    <div *ngIf="p.current !== null || p.proposed !== null" class="rc__diff">
                        <div class="rc__diff-col rc__diff-col--current">
                            <div class="rc__diff-label">Current</div>
                            <div class="rc__diff-body">{{ p.current || '(empty)' }}</div>
                        </div>
                        <div class="rc__diff-col rc__diff-col--proposed">
                            <div class="rc__diff-label">Proposed</div>
                            <div class="rc__diff-body">{{ p.proposed || '(empty)' }}</div>
                        </div>
                    </div>

                    <div *ngIf="p.footer" class="rc__footer-note"><i class="fa-solid fa-circle-info"></i> {{ p.footer }}</div>
                </ng-container>

                <!-- attribution -->
                <div class="rc__meta">
                    <span *ngIf="r.GeneratedByAgentRun">From: {{ r.GeneratedByAgentRun }}</span>
                    <span *ngIf="r.Status === 'Applied' && r.AppliedByUser">Applied by {{ r.AppliedByUser }}</span>
                    <span *ngIf="r.Status === 'Dismissed' && r.DismissedByUser">Dismissed by {{ r.DismissedByUser }}</span>
                </div>

                <!-- actions — Open rows only; status flip, never auto-applies a diff -->
                <div *ngIf="r.Status === 'Open' && canWrite" class="rc__actions">
                    <button type="button" class="rc__btn rc__btn--apply" [disabled]="savingId === r.ID" (click)="apply(r)">
                        <i class="fa-solid fa-check"></i> Mark Applied
                    </button>
                    <button type="button" class="rc__btn rc__btn--dismiss" [disabled]="savingId === r.ID" (click)="dismiss(r)">
                        <i class="fa-solid fa-xmark"></i> Dismiss
                    </button>
                    <span *ngIf="p2(r).current !== null" class="rc__apply-note">
                        Marking Applied does not change the live strategy section — apply the diff in the Strategy doc.
                    </span>
                </div>
            </div>
        </div>
    </div>
</ng-container>
    `,
    styles: [`
        :host { display: block; }
        .rc__loading { padding: 60px 20px; text-align: center; color: var(--mj-text-secondary, #71717a); }

        .rc__kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px; }
        .rc__kpi { background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 10px; padding: 14px 16px; cursor: pointer; transition: border-color 0.15s; }
        .rc__kpi:hover { border-color: var(--mj-text-primary, #1a1a1a); }
        .rc__kpi--alert .rc__kpi-value { color: var(--mj-status-warning, #b45309); }
        .rc__kpi-value { font-size: 26px; font-weight: 600; color: var(--mj-text-primary, #1a1a1a); font-variant-numeric: tabular-nums; line-height: 1.1; }
        .rc__kpi-label { font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); margin-top: 6px; }

        .rc__pills { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); }
        .rc__pill { display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 99px; background: var(--mj-bg-surface, #fff); color: var(--mj-text-secondary, #71717a); font-size: 12px; font-weight: 500; font-family: inherit; cursor: pointer; transition: background 0.15s, color 0.15s, border-color 0.15s; }
        .rc__pill:hover { color: var(--mj-text-primary, #1a1a1a); }
        .rc__pill--active { background: var(--mj-brand-primary, #6366f1); color: var(--mj-text-inverse, #fff); border-color: var(--mj-brand-primary, #6366f1); }

        .rc__list { display: flex; flex-direction: column; gap: 8px; }
        .rc__row { background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 10px; overflow: hidden; }
        .rc__row--open { border-color: var(--mj-text-primary, #1a1a1a); }
        .rc__row-head { display: flex; align-items: center; gap: 10px; width: 100%; padding: 12px 14px; background: none; border: none; font-family: inherit; cursor: pointer; text-align: left; }
        .rc__row-head:hover { background: var(--mj-bg-page, #f5f5f5); }
        .rc__title { flex: 1; font-size: 13px; font-weight: 500; color: var(--mj-text-primary, #1a1a1a); min-width: 0; }
        .rc__row-head > i { font-size: 11px; color: var(--mj-text-secondary, #71717a); }

        .rc__detail { padding: 4px 16px 16px; border-top: 1px solid var(--mj-border-default, #e0e0e0); }
        .rc__pre { font-size: 12.5px; line-height: 1.5; color: var(--mj-text-secondary, #71717a); white-space: pre-wrap; margin: 12px 0; }

        .rc__diff { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 12px 0; }
        .rc__diff-col { border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 8px; padding: 10px 12px; }
        .rc__diff-col--current { background: var(--mj-color-error-50, #fff1f2); border-color: var(--mj-color-error-100, #fecdd3); }
        .rc__diff-col--proposed { background: var(--mj-color-success-50, #ecfdf5); border-color: var(--mj-color-success-100, #d1fae5); }
        .rc__diff-label { font-size: 10.5px; letter-spacing: 0.06em; text-transform: uppercase; font-weight: 600; color: var(--mj-text-secondary, #71717a); margin-bottom: 6px; }
        .rc__diff-body { font-size: 12.5px; line-height: 1.5; color: var(--mj-text-primary, #1a1a1a); white-space: pre-wrap; }

        .rc__footer-note { display: flex; gap: 6px; align-items: baseline; font-size: 12px; color: var(--mj-text-secondary, #71717a); margin: 10px 0; }
        .rc__meta { display: flex; flex-wrap: wrap; gap: 14px; font-size: 11.5px; color: var(--mj-text-secondary, #71717a); margin: 12px 0 0; }

        .rc__actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 14px; padding-top: 12px; border-top: 1px dashed var(--mj-border-default, #e0e0e0); }
        .rc__btn { display: inline-flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: 8px; font-size: 12.5px; font-weight: 500; font-family: inherit; cursor: pointer; border: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); }
        .rc__btn:disabled { opacity: 0.5; cursor: default; }
        .rc__btn--apply { color: var(--mj-color-success-700, #047857); border-color: var(--mj-color-success-100, #d1fae5); }
        .rc__btn--apply:hover:not(:disabled) { background: var(--mj-color-success-50, #ecfdf5); }
        .rc__btn--dismiss { color: var(--mj-status-error, #be123c); border-color: var(--mj-color-error-100, #fecdd3); }
        .rc__btn--dismiss:hover:not(:disabled) { background: var(--mj-color-error-50, #fff1f2); }
        .rc__apply-note { font-size: 11px; color: var(--mj-text-secondary, #71717a); }

        @media (max-width: 900px) { .rc__kpis { grid-template-columns: repeat(2, 1fr); } .rc__diff { grid-template-columns: 1fr; } }
    `]
})
export class StrategyRecommendationsSection {
    @Input() companyId = '';
    @Input() companyName = '';
    /**
     * Open + recently-actioned recommendations, loaded once by the workspace
     * shell as entity_object (so .Save() works here). The shell's current load
     * filters Status='Open'; this section still buckets by Status so it stays
     * correct if the shell widens that filter later.
     */
    @Input() recommendations: marketingMarketingRecommendationEntity[] = [];
    @Input() isLoadingContent = false;
    /** Active user's marketing role; null = system/Owner (full access). #938. */
    @Input() userRole: RoleTier | null = null;

    private cdr = inject(ChangeDetectorRef);

    filter: RecommendationsFilter = 'open';
    expandedId: string | null = null;
    savingId: string | null = null;

    // Computed counts ─────────────────────────────────
    get openCount(): number { return this.recommendations.filter(r => r.Status === 'Open').length; }
    get appliedCount(): number { return this.recommendations.filter(r => r.Status === 'Applied').length; }
    get dismissedCount(): number { return this.recommendations.filter(r => r.Status === 'Dismissed').length; }

    get filteredRecommendations(): marketingMarketingRecommendationEntity[] {
        switch (this.filter) {
            case 'open': return this.recommendations.filter(r => r.Status === 'Open');
            case 'applied': return this.recommendations.filter(r => r.Status === 'Applied');
            case 'dismissed': return this.recommendations.filter(r => r.Status === 'Dismissed');
            default: return this.recommendations;
        }
    }

    /** True if the active user has Admin rank (or is a system user). */
    get canWrite(): boolean {
        if (this.userRole === null) return true;
        return ROLE_RANK[this.userRole] >= ROLE_RANK['Admin'];
    }

    setFilter(f: RecommendationsFilter): void {
        this.filter = f;
        this.expandedId = null;
        this.cdr.detectChanges();
    }

    toggle(id: string): void {
        this.expandedId = this.expandedId === id ? null : id;
        this.cdr.detectChanges();
    }

    /**
     * Apply = adopt the recommendation. Status flip + actor/timestamp stamp
     * ONLY. For section-adaptation proposals (which carry a diff) this does NOT
     * mutate the live Strategy Section — applying the diff is a separate edit in
     * the Strategy doc. The UI surfaces that caveat next to the button.
     */
    async apply(rec: marketingMarketingRecommendationEntity): Promise<void> {
        await this.transition(rec, 'Applied');
    }

    async dismiss(rec: marketingMarketingRecommendationEntity): Promise<void> {
        await this.transition(rec, 'Dismissed');
    }

    private async transition(rec: marketingMarketingRecommendationEntity, status: 'Applied' | 'Dismissed'): Promise<void> {
        if (!this.canWrite) {
            LogError(`[StrategyRecommendations] ${status} blocked: role '${this.userRole}' lacks Admin rank`);
            return;
        }
        if (rec.Status !== 'Open') return;
        this.savingId = rec.ID;
        this.cdr.detectChanges();
        try {
            const md = new Metadata();
            const currentUserId = md.CurrentUser?.ID ?? null;
            const now = new Date();
            rec.Status = status;
            if (status === 'Applied') {
                if (currentUserId) rec.AppliedByUserID = currentUserId;
                rec.AppliedAt = now;
            } else {
                if (currentUserId) rec.DismissedByUserID = currentUserId;
                rec.DismissedAt = now;
            }
            const saved = await rec.Save();
            if (!saved) {
                LogError(`[StrategyRecommendations] save failed for recommendation ${rec.ID}: ${rec.LatestResult?.Message ?? 'unknown'}`);
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[StrategyRecommendations] transition to ${status} threw for ${rec.ID}: ${msg}`);
        } finally {
            this.savingId = null;
            this.cdr.detectChanges();
        }
    }

    categoryVariant(category: string): PillVariant {
        switch (category) {
            case 'Experiment': return 'info';
            case 'Channel': return 'warn';
            case 'Spend': return 'bad';
            case 'Content': return 'good';
            default: return 'muted';
        }
    }

    statusVariant(status: string): PillVariant {
        switch (status) {
            case 'Applied': return 'good';
            case 'Dismissed': return 'muted';
            default: return 'warn';
        }
    }

    /**
     * Split a recommendation's Detail into preamble / current / proposed /
     * footer when it carries the agent's `--- Current Body ---` /
     * `--- Proposed Body ---` diff markers. Without markers the whole Detail is
     * the preamble (e.g. outbound sequence learnings: finding + evidence).
     */
    parsed(rec: marketingMarketingRecommendationEntity): ParsedDetail {
        const detail = rec.Detail ?? '';
        const CUR = '--- Current Body ---';
        const PROP = '--- Proposed Body ---';
        const curIdx = detail.indexOf(CUR);
        const propIdx = detail.indexOf(PROP);
        if (curIdx === -1 || propIdx === -1 || propIdx < curIdx) {
            return { preamble: detail.trim(), current: null, proposed: null, footer: null };
        }
        const preamble = detail.slice(0, curIdx).trim();
        const current = detail.slice(curIdx + CUR.length, propIdx).trim();
        let proposed = detail.slice(propIdx + PROP.length).trim();
        // The action appends a trailing "To apply: …" line after the proposed body.
        let footer: string | null = null;
        const applyIdx = proposed.indexOf('To apply:');
        if (applyIdx !== -1) {
            footer = proposed.slice(applyIdx).trim();
            proposed = proposed.slice(0, applyIdx).trim();
        }
        return { preamble, current, proposed, footer };
    }

    /** Template helper: parsed result for the apply-note guard (avoids re-parse in *ngIf). */
    p2(rec: marketingMarketingRecommendationEntity): ParsedDetail {
        return this.parsed(rec);
    }
}
